import { memo, ReactElement, ReactNode, useCallback } from "react";

import TableHeadColumn from "ds/components/Table/Head/Column";
import { TableColumnMeta } from "ds/components/Table/types";
import Box from "ds/components/Box";
import TableHeadColumnTitle from "ds/components/Table/Head/Column/Title";
import TableHeadColumnOptionsDropdown from "ds/components/Table/Head/Column/Options";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import { useResizer } from "ds/components/Table/Head/Column/useResizer";
import useColumnSort from "ds/components/Table/Head/Column/useColumnSort";
import { SortStateDescriptor } from "components/Filters/types";
import useAnalytics, { AnalyticsPage } from "hooks/useAnalytics";

const COLUMN_CHANGED_ANALYTIC_EVENT = "Column changed";

const canMoveLeft = (columnId: string, columnsOrder: string[]) => {
  return columnsOrder.indexOf(columnId) > 1; // skip name (first column)
};

const canMoveRight = (columnId: string, columnsOrder: string[]) => {
  return columnsOrder.indexOf(columnId) < columnsOrder.length - 2; // skip options (last column)
};

type TableHeadColumnWithActionsProps<T extends string> = {
  sortCallback?: (sortDescriptor: SortStateDescriptor) => void;
  column: TableColumnMeta<T>;
  stickyLeft?: boolean;
  stickyRight?: boolean;
  children?: ReactNode;
  hideColumn?: (columnId: T) => void;
  columnSizes: Record<T, number>;
  columnsOrder: {
    visible: T[];
    hidden: T[];
  };
  setColumnSize: (columnId: T, size: number) => void;
  reorderVisibleColumns?: (columnId: T, direction: "left" | "right") => void;
  analyticsPage: AnalyticsPage;
  sortConfig?: Record<string, string>;
  filterOrderSettingsKey: string;
};

const TableHeadColumnWithActions = <T extends string>({
  sortCallback,
  column,
  stickyLeft,
  stickyRight,
  children,
  hideColumn,
  columnSizes,
  columnsOrder,
  setColumnSize,
  reorderVisibleColumns,
  analyticsPage,
  sortConfig,
  filterOrderSettingsKey,
}: TableHeadColumnWithActionsProps<T>) => {
  const trackAnalytics = useAnalytics({ page: analyticsPage });

  const saveColumnSize = useCallback(
    (width: number) => {
      trackAnalytics(COLUMN_CHANGED_ANALYTIC_EVENT, {
        column: column.id,
        action: "resize",
      });
      setColumnSize(column.id, width);
    },
    [column.id, setColumnSize, trackAnalytics]
  );

  const { ariaSort, handleSort, icon } = useColumnSort(
    sortConfig?.[column.id] || column.id,
    filterOrderSettingsKey
  );

  const savedColumnWidth = columnSizes[column.id];

  const { ResizerControl, columnRef } = useResizer({
    width: savedColumnWidth,
    minWidth: column.minWidth,
    maxWidth: column.maxWidth,
    onResizeClb: saveColumnSize,
  });

  const onSort = useCallback(() => {
    handleSort(sortCallback);
  }, [handleSort, sortCallback]);
  const { resizable, canHide } = column;

  return (
    <TableHeadColumn
      ref={columnRef}
      column={column}
      stickyLeft={stickyLeft}
      stickyRight={stickyRight}
      ariaSort={ariaSort}
    >
      {children}
      {!children && column.noHeader && <span className="sr-only">{column.title}</span>}
      {!children && !column.noHeader && (
        <Box gap="small" align="center" justify="between" fullHeight>
          <TableHeadColumnTitle
            title={column.title}
            allowsSorting={column.allowsSorting}
            sortIcon={icon}
            onSort={onSort}
          />

          {(canHide || column.resizable) && (
            <Box align="center">
              {column.canHide && !!hideColumn && (
                <TableHeadColumnOptionsDropdown>
                  <DropdownMenuItem
                    onAction={() => hideColumn(column.id)}
                    analyticsPage={analyticsPage}
                    analyticsTitle={COLUMN_CHANGED_ANALYTIC_EVENT}
                    analyticsProps={{ column: column.id, action: "hide" }}
                  >
                    Hide
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onAction={() => reorderVisibleColumns?.(column.id, "left")}
                    isDisabled={!canMoveLeft(column.id, columnsOrder.visible)}
                    analyticsPage={analyticsPage}
                    analyticsTitle={COLUMN_CHANGED_ANALYTIC_EVENT}
                    analyticsProps={{ column: column.id, action: "move left" }}
                  >
                    Move left
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onAction={() => reorderVisibleColumns?.(column.id, "right")}
                    isDisabled={!canMoveRight(column.id, columnsOrder.visible)}
                    analyticsPage={analyticsPage}
                    analyticsTitle={COLUMN_CHANGED_ANALYTIC_EVENT}
                    analyticsProps={{ column: column.id, action: "move right" }}
                  >
                    Move right
                  </DropdownMenuItem>
                </TableHeadColumnOptionsDropdown>
              )}

              {resizable && ResizerControl}
            </Box>
          )}
        </Box>
      )}
    </TableHeadColumn>
  );
};

TableHeadColumnWithActions.displayName = "DS.Table.Head.ColumnWithActions";

export default memo(TableHeadColumnWithActions) as <T extends string>(
  p: TableHeadColumnWithActionsProps<T>
) => ReactElement;
